import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { abortRace } from "@redotech/util/abort";
import type { ChatTransport, UIMessageChunk } from "ai";
import type { AskRedoUIMessage } from "../app/ask-redo/types";

export interface ExtensionArtifactAgentCoderTransportOptions {
  /**
   * Read at send time so each turn ships the editor's CURRENT contract,
   * selector, and code — the merchant can hand-edit the code (or change the
   * selector) between turns, and the Chat/transport pair outlives renders.
   */
  getEditorState: () => {
    contractId: string;
    inputSelector: string[];
    code: string;
  };
  /** Called when the turn's `complete` event delivers the edited code. */
  onCode: (code: string) => void;
}

/**
 * Bridges the streamExtensionArtifactAgentCoder RPC generator into the AI SDK
 * `ChatTransport` interface so `useChat` (and the Ask Redo chat components)
 * can drive the Agent Coder pane. A simplified sibling of AskRedoRpcTransport —
 * same event translation, none of the escalation/approval/page-context
 * machinery.
 */
export class ExtensionArtifactAgentCoderTransport implements ChatTransport<AskRedoUIMessage> {
  /** Server-assigned LlmThread id, captured from the first turn's
   *  `complete` event; later turns continue the same thread. */
  private threadId: string | undefined;

  constructor(
    private readonly rpcClient: RedoMerchantRpcClient,
    private readonly options: ExtensionArtifactAgentCoderTransportOptions,
  ) {}

  async sendMessages(options: {
    chatId: string;
    messages: AskRedoUIMessage[];
    abortSignal?: AbortSignal;
    trigger: "submit-message" | "regenerate-message" | "resume-stream";
    messageId?: string;
    metadata?: unknown;
  }): Promise<ReadableStream<UIMessageChunk>> {
    const message = lastUserMessageText(options.messages);
    const { contractId, inputSelector, code } = this.options.getEditorState();

    const cancelController = new AbortController();
    const combinedSignal = options.abortSignal
      ? abortRace([options.abortSignal, cancelController.signal])
      : cancelController.signal;

    const stream = this.rpcClient.streamExtensionArtifactAgentCoder(
      { threadId: this.threadId, message, contractId, inputSelector, code },
      { signal: combinedSignal },
    );

    const messageId = options.messageId ?? crypto.randomUUID();
    const captureComplete = (threadId: string, newCode: string) => {
      this.threadId = threadId;
      this.options.onCode(newCode);
    };

    return new ReadableStream<UIMessageChunk>({
      async start(controller) {
        let closed = false;
        const safeEnqueue = (chunk: UIMessageChunk) => {
          if (closed) {
            return;
          }
          controller.enqueue(chunk);
        };
        const safeClose = () => {
          if (closed) {
            return;
          }
          closed = true;
          controller.close();
        };

        safeEnqueue({ type: "start", messageId });
        safeEnqueue({ type: "start-step" });

        let currentTextId: string | undefined;
        let currentReasoningId: string | undefined;
        const closeOpenText = () => {
          if (currentTextId) {
            safeEnqueue({ type: "text-end", id: currentTextId });
            currentTextId = undefined;
          }
        };
        const closeOpenReasoning = () => {
          if (currentReasoningId) {
            safeEnqueue({ type: "reasoning-end", id: currentReasoningId });
            currentReasoningId = undefined;
          }
        };

        try {
          for await (const event of stream) {
            switch (event.type) {
              case "text-delta": {
                closeOpenReasoning();
                if (!currentTextId) {
                  currentTextId = crypto.randomUUID();
                  safeEnqueue({ type: "text-start", id: currentTextId });
                }
                safeEnqueue({
                  type: "text-delta",
                  id: currentTextId,
                  delta: event.textDelta,
                });
                break;
              }
              case "reasoning": {
                if (!currentReasoningId) {
                  currentReasoningId = crypto.randomUUID();
                  safeEnqueue({
                    type: "reasoning-start",
                    id: currentReasoningId,
                  });
                }
                safeEnqueue({
                  type: "reasoning-delta",
                  id: currentReasoningId,
                  delta: event.text,
                });
                break;
              }
              case "tool-input-start": {
                closeOpenText();
                safeEnqueue({
                  type: "tool-input-start",
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  dynamic: true,
                });
                break;
              }
              case "tool-input-delta": {
                safeEnqueue({
                  type: "tool-input-delta",
                  toolCallId: event.toolCallId,
                  inputTextDelta: event.inputTextDelta,
                });
                break;
              }
              case "tool-call": {
                closeOpenText();
                safeEnqueue({
                  type: "tool-input-available",
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  input: event.input,
                  dynamic: true,
                });
                break;
              }
              case "tool-output-available": {
                safeEnqueue({
                  type: "tool-output-available",
                  toolCallId: event.toolCallId,
                  output: event.output,
                  dynamic: true,
                });
                break;
              }
              case "tool-output-error": {
                safeEnqueue({
                  type: "tool-output-error",
                  toolCallId: event.toolCallId,
                  errorText: event.errorText,
                  dynamic: true,
                });
                break;
              }
              case "complete": {
                captureComplete(event.threadId, event.code);
                break;
              }
              case "error": {
                safeEnqueue({ type: "error", errorText: event.error });
                break;
              }
            }
          }
          closeOpenText();
          closeOpenReasoning();
          safeEnqueue({ type: "finish-step" });
          safeEnqueue({ type: "finish" });
        } catch (err) {
          closeOpenText();
          closeOpenReasoning();
          safeEnqueue({
            type: "error",
            errorText: err instanceof Error ? err.message : String(err),
          });
          safeEnqueue({ type: "finish-step" });
          safeEnqueue({ type: "finish" });
        } finally {
          safeClose();
        }
      },
      async cancel() {
        cancelController.abort();
      },
    });
  }

  async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
    // LlmThread is the checkpoint; a fresh sendMessages with the captured
    // threadId continues the conversation.
    return null;
  }
}

function lastUserMessageText(messages: AskRedoUIMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message || message.role !== "user") {
      continue;
    }
    return message.parts
      .filter((part) => part.type === "text")
      .map((part) => part.text)
      .join("\n");
  }
  return "";
}
